import { useEffect, useRef } from 'react';
import { Group } from 'three';

import type {
  CharacterDefinition,
  CharacterPoseLibrary,
} from '../../content/schema/character';
import {
  applyPose,
  registerCharacterRenderers,
  type CharacterRendererProps,
} from '../rendererRegistry';
import { registerRigBones } from '../rigBonesRegistry';

/** Rig id for the engine's neutral biped. Poses must match to apply. */
export const NEUTRAL_BIPED_RIG_ID = 'core-biped-rig-v1';

/** Renderer id registered for the neutral biped. */
export const NEUTRAL_BIPED_RENDERER_ID = 'core-biped-renderer';

/**
 * Ordered bone ids for the neutral biped rig. Character Studio uses this to lay
 * out posing controls; the renderer below reads the same ids via `applyPose`.
 */
export const CORE_RIG_BONES: Record<string, string[]> = {
  [NEUTRAL_BIPED_RIG_ID]: ['hips', 'torso', 'head', 'left-arm', 'right-arm', 'left-leg', 'right-leg'],
};

const SKIN = '#9aa6b4';
const SKIN_DARK = '#7e8a98';
const JOINT = '#6c7682';

/**
 * A clean, license-free capsule humanoid. Neutral palette and proportions so it
 * reads as a placeholder/biped for any project. Bone ids match `CORE_RIG_BONES`
 * so the shared pose library and Character Studio drive it directly.
 */
function NeutralBipedRenderable({ pose }: CharacterRendererProps) {
  const hipsRef = useRef<Group>(null);
  const torsoRef = useRef<Group>(null);
  const headRef = useRef<Group>(null);
  const leftArmRef = useRef<Group>(null);
  const rightArmRef = useRef<Group>(null);
  const leftLegRef = useRef<Group>(null);
  const rightLegRef = useRef<Group>(null);

  useEffect(() => {
    applyPose(hipsRef.current, pose, 'hips');
    applyPose(torsoRef.current, pose, 'torso');
    applyPose(headRef.current, pose, 'head');
    applyPose(leftArmRef.current, pose, 'left-arm', { rotation: [0, 0, 0.08] });
    applyPose(rightArmRef.current, pose, 'right-arm', { rotation: [0, 0, -0.08] });
    applyPose(leftLegRef.current, pose, 'left-leg');
    applyPose(rightLegRef.current, pose, 'right-leg');
  }, [pose]);

  return (
    <group ref={hipsRef} position={[0, 0.92, 0]}>
      {/* Pelvis */}
      <mesh castShadow receiveShadow>
        <capsuleGeometry args={[0.16, 0.12, 6, 14]} />
        <meshStandardMaterial color={SKIN_DARK} roughness={0.7} metalness={0.05} />
      </mesh>

      {/* Legs (rooted at the pelvis, hanging down) */}
      <group ref={leftLegRef} position={[-0.11, -0.06, 0]}>
        <mesh castShadow receiveShadow position={[0, -0.42, 0]}>
          <capsuleGeometry args={[0.085, 0.74, 6, 12]} />
          <meshStandardMaterial color={SKIN} roughness={0.72} metalness={0.05} />
        </mesh>
        <mesh castShadow receiveShadow position={[0, -0.86, 0.04]}>
          <boxGeometry args={[0.17, 0.09, 0.32]} />
          <meshStandardMaterial color={JOINT} roughness={0.8} metalness={0.04} />
        </mesh>
      </group>
      <group ref={rightLegRef} position={[0.11, -0.06, 0]}>
        <mesh castShadow receiveShadow position={[0, -0.42, 0]}>
          <capsuleGeometry args={[0.085, 0.74, 6, 12]} />
          <meshStandardMaterial color={SKIN} roughness={0.72} metalness={0.05} />
        </mesh>
        <mesh castShadow receiveShadow position={[0, -0.86, 0.04]}>
          <boxGeometry args={[0.17, 0.09, 0.32]} />
          <meshStandardMaterial color={JOINT} roughness={0.8} metalness={0.04} />
        </mesh>
      </group>

      {/* Torso */}
      <group ref={torsoRef} position={[0, 0.12, 0]}>
        <mesh castShadow receiveShadow position={[0, 0.3, 0]}>
          <capsuleGeometry args={[0.21, 0.46, 6, 16]} />
          <meshStandardMaterial color={SKIN} roughness={0.7} metalness={0.06} />
        </mesh>

        {/* Arms (rooted at the shoulders, hanging down) */}
        <group ref={leftArmRef} position={[-0.26, 0.5, 0]}>
          <mesh castShadow receiveShadow position={[0, -0.34, 0]}>
            <capsuleGeometry args={[0.066, 0.6, 6, 12]} />
            <meshStandardMaterial color={SKIN} roughness={0.72} metalness={0.05} />
          </mesh>
          <mesh castShadow receiveShadow position={[0, -0.72, 0]}>
            <sphereGeometry args={[0.072, 12, 12]} />
            <meshStandardMaterial color={JOINT} roughness={0.78} metalness={0.05} />
          </mesh>
        </group>
        <group ref={rightArmRef} position={[0.26, 0.5, 0]}>
          <mesh castShadow receiveShadow position={[0, -0.34, 0]}>
            <capsuleGeometry args={[0.066, 0.6, 6, 12]} />
            <meshStandardMaterial color={SKIN} roughness={0.72} metalness={0.05} />
          </mesh>
          <mesh castShadow receiveShadow position={[0, -0.72, 0]}>
            <sphereGeometry args={[0.072, 12, 12]} />
            <meshStandardMaterial color={JOINT} roughness={0.78} metalness={0.05} />
          </mesh>
        </group>

        {/* Head */}
        <group ref={headRef} position={[0, 0.66, 0]}>
          <mesh castShadow receiveShadow position={[0, 0.04, 0]}>
            <cylinderGeometry args={[0.06, 0.075, 0.12, 12]} />
            <meshStandardMaterial color={SKIN_DARK} roughness={0.74} metalness={0.05} />
          </mesh>
          <mesh castShadow receiveShadow position={[0, 0.24, 0]}>
            <sphereGeometry args={[0.165, 20, 20]} />
            <meshStandardMaterial color={SKIN} roughness={0.68} metalness={0.06} />
          </mesh>
        </group>
      </group>
    </group>
  );
}

/** The neutral biped's character definition, registered as a Studio default. */
export const NEUTRAL_BIPED_DEFINITION: CharacterDefinition = {
  id: 'core-biped',
  name: 'Neutral Biped',
  kind: 'player',
  rigId: NEUTRAL_BIPED_RIG_ID,
  rendererId: NEUTRAL_BIPED_RENDERER_ID,
  description: 'A neutral capsule humanoid for posing and as a project default.',
  defaultPoseId: 'biped-idle',
  poseLibraryId: 'core-biped-poses',
  tags: ['core', 'biped', 'humanoid'],
};

/** Starter pose library for the neutral biped rig. */
export const NEUTRAL_BIPED_POSE_LIBRARY: CharacterPoseLibrary = {
  id: 'core-biped-poses',
  rigId: NEUTRAL_BIPED_RIG_ID,
  poses: [
    {
      id: 'biped-idle',
      name: 'Idle',
      rigId: NEUTRAL_BIPED_RIG_ID,
      description: 'Neutral standing rest pose.',
      tags: ['default'],
      boneTransforms: {
        hips: { rotation: [0, 0, 0] },
        torso: { rotation: [0, 0, 0] },
        head: { rotation: [0, 0, 0] },
        'left-arm': { rotation: [0, 0, 0.08] },
        'right-arm': { rotation: [0, 0, -0.08] },
        'left-leg': { rotation: [0, 0, 0] },
        'right-leg': { rotation: [0, 0, 0] },
      },
    },
    {
      id: 'biped-tpose',
      name: 'T-Pose',
      rigId: NEUTRAL_BIPED_RIG_ID,
      description: 'Reference T-pose with arms out.',
      tags: ['reference'],
      boneTransforms: {
        hips: { rotation: [0, 0, 0] },
        torso: { rotation: [0, 0, 0] },
        head: { rotation: [0, 0, 0] },
        'left-arm': { rotation: [0, 0, 1.5] },
        'right-arm': { rotation: [0, 0, -1.5] },
        'left-leg': { rotation: [0, 0, 0] },
        'right-leg': { rotation: [0, 0, 0] },
      },
    },
    {
      id: 'biped-walk',
      name: 'Walk',
      rigId: NEUTRAL_BIPED_RIG_ID,
      description: 'Mid-stride walk pose.',
      tags: ['movement'],
      boneTransforms: {
        hips: { rotation: [0, 0, 0] },
        torso: { rotation: [0.06, 0, 0] },
        head: { rotation: [-0.04, 0, 0] },
        'left-arm': { rotation: [0.5, 0, 0.08] },
        'right-arm': { rotation: [-0.5, 0, -0.08] },
        'left-leg': { rotation: [-0.45, 0, 0] },
        'right-leg': { rotation: [0.45, 0, 0] },
      },
    },
    {
      id: 'biped-sit',
      name: 'Sit',
      rigId: NEUTRAL_BIPED_RIG_ID,
      description: 'Seated pose.',
      tags: ['idle'],
      boneTransforms: {
        hips: { rotation: [0, 0, 0], position: [0, -0.42, 0] },
        torso: { rotation: [0.08, 0, 0] },
        head: { rotation: [0, 0, 0] },
        'left-arm': { rotation: [-0.2, 0, 0.12] },
        'right-arm': { rotation: [-0.2, 0, -0.12] },
        'left-leg': { rotation: [1.4, 0, 0] },
        'right-leg': { rotation: [1.4, 0, 0] },
      },
    },
    {
      id: 'biped-wave',
      name: 'Wave',
      rigId: NEUTRAL_BIPED_RIG_ID,
      description: 'Friendly waving pose.',
      tags: ['gesture'],
      boneTransforms: {
        hips: { rotation: [0, 0, 0] },
        torso: { rotation: [0, 0.06, 0] },
        head: { rotation: [0, 0.1, 0] },
        'left-arm': { rotation: [0, 0, 2.4] },
        'right-arm': { rotation: [0, 0, -0.08] },
        'left-leg': { rotation: [0, 0, 0] },
        'right-leg': { rotation: [0, 0, 0] },
      },
    },
  ],
};

/**
 * Register the engine's neutral biped renderer. Call once at startup (the core
 * pack does this) so `core-biped` characters resolve a renderer.
 */
export function registerCoreCharacters(): void {
  registerCharacterRenderers({ [NEUTRAL_BIPED_RENDERER_ID]: NeutralBipedRenderable });
  registerRigBones(NEUTRAL_BIPED_RIG_ID, CORE_RIG_BONES[NEUTRAL_BIPED_RIG_ID]!);
}
